// ============================================================================
// Theme engine
// ----------------------------------------------------------------------------
// Applies the light/dark theme by toggling `data-theme` on <html>. The chosen
// mode is one of: "light", "dark", or "system". When "system", we follow the
// OS `prefers-color-scheme` and keep reacting to changes live. As soon as the
// user picks light or dark explicitly, we stop following the OS and remember
// their choice in localStorage so it survives reloads and future sessions.
//
// Storage:
//   - THEME_MODE_KEY holds the user's preference: "light" | "dark" | "system".
//     Absence of this key (first-ever load) is treated as "light" — the app
//     defaults to light theme. "system" is still supported if the user
//     explicitly chooses to follow the OS, but it is not the default.
//
// The very first paint is handled by an inline snippet in public/index.html so
// there is no flash of the wrong theme before React mounts; this module keeps
// everything in sync afterwards.
// ============================================================================

export const THEME_MODE_KEY = "app_theme_mode";

const MEDIA_QUERY = "(prefers-color-scheme: dark)";

/** Read the persisted theme mode. Defaults to "light" when unset/invalid. */
export function getStoredThemeMode() {
  try {
    const v = localStorage.getItem(THEME_MODE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch (_) {}
  return "light";
}

/** True if the OS currently prefers a dark color scheme. */
export function systemPrefersDark() {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(MEDIA_QUERY).matches
    );
  } catch (_) {
    return false;
  }
}

/** Resolve a mode ("light" | "dark" | "system") to the effective theme. */
export function resolveTheme(mode) {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  return systemPrefersDark() ? "dark" : "light";
}

/** Apply an effective theme ("light" | "dark") to the document element. */
export function applyTheme(effective) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (effective === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
  // Keep the native UI (form controls, scrollbars) aligned with the theme.
  root.style.colorScheme = effective === "dark" ? "dark" : "light";
}

let mediaListenerAttached = false;
let mediaQueryList = null;
const listeners = new Set();

function handleSystemChange() {
  // Only react to OS changes while the user is in "system" mode.
  if (getStoredThemeMode() !== "system") return;
  const effective = systemPrefersDark() ? "dark" : "light";
  applyTheme(effective);
  listeners.forEach((cb) => {
    try {
      cb(effective);
    } catch (_) {}
  });
}

function ensureSystemListener() {
  if (mediaListenerAttached) return;
  try {
    mediaQueryList = window.matchMedia(MEDIA_QUERY);
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleSystemChange);
    } else if (mediaQueryList.addListener) {
      // Safari < 14
      mediaQueryList.addListener(handleSystemChange);
    }
    mediaListenerAttached = true;
  } catch (_) {}
}

/**
 * Subscribe to effective-theme changes triggered by the OS (only relevant in
 * "system" mode). Returns an unsubscribe function.
 */
export function subscribeToSystemTheme(cb) {
  listeners.add(cb);
  ensureSystemListener();
  return () => listeners.delete(cb);
}

/**
 * Persist and apply a new theme mode.
 * @param {"light"|"dark"|"system"} mode
 * @returns {"light"|"dark"} the effective theme that was applied
 */
export function setThemeMode(mode) {
  const safeMode =
    mode === "light" || mode === "dark" || mode === "system" ? mode : "light";
  try {
    localStorage.setItem(THEME_MODE_KEY, safeMode);
  } catch (_) {}
  const effective = resolveTheme(safeMode);
  applyTheme(effective);
  return effective;
}

/**
 * Initialize the theme on app start: apply the stored (or system) theme and
 * wire up the OS listener. Safe to call more than once.
 * @returns {{ mode: string, effective: "light"|"dark" }}
 */
export function initTheme() {
  const mode = getStoredThemeMode();
  const effective = resolveTheme(mode);
  applyTheme(effective);
  ensureSystemListener();
  return { mode, effective };
}

export default {
  THEME_MODE_KEY,
  getStoredThemeMode,
  systemPrefersDark,
  resolveTheme,
  applyTheme,
  setThemeMode,
  initTheme,
  subscribeToSystemTheme,
};
